import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    output,
    viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icons/icon/icon.component';

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, ButtonComponent, IconComponent],
})
export class ModalComponent {
    readonly open = input<boolean>(false);
    readonly title = input<string>('');

    readonly showCancel = input<boolean>(true);
    readonly showConfirm = input<boolean>(true);
    readonly showCloseIcon = input<boolean>(true);

    readonly cancelLabel = input<string>('CANCEL');
    readonly confirmLabel = input<string>('SAVE');

    readonly cancelDisabled = input<boolean>(false);
    readonly confirmDisabled = input<boolean>(false);
    readonly confirmLoading = input<boolean>(false);
    readonly confirmHint = input<string>('');

    readonly disableBackdropClose = input<boolean>(false);

    readonly cancelled = output<void>();
    readonly confirmed = output<void>();
    readonly closed = output<void>();
    readonly confirmHintVisible = output<boolean>();

    protected readonly hasActionButtons = computed(() => this.showCancel() || this.showConfirm());

    protected setConfirmHintVisible(visible: boolean): void {
        if (this.confirmHint()) {
            this.confirmHintVisible.emit(visible);
        }
    }

    private readonly dialogRef = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

    private static openCount = 0;

    constructor() {
        effect(() => {
            const dialog = this.dialogRef().nativeElement;
            if (this.open() && !dialog.open) {
                dialog.showModal();
                ModalComponent.lockBodyScroll();
            } else if (!this.open() && dialog.open) {
                dialog.close();
                ModalComponent.releaseBodyScroll();
            }
        });

        inject(DestroyRef).onDestroy(() => {
            if (this.dialogRef().nativeElement.open) {
                ModalComponent.releaseBodyScroll();
            }
        });
    }

    private static lockBodyScroll() {
        ModalComponent.openCount++;
        if (ModalComponent.openCount === 1) {
            document.body.style.overflow = 'hidden';
        }
    }

    private static releaseBodyScroll() {
        ModalComponent.openCount = Math.max(0, ModalComponent.openCount - 1);
        if (ModalComponent.openCount === 0) {
            document.body.style.overflow = '';
        }
    }

    protected onBackdropClick(event: MouseEvent) {
        if (this.disableBackdropClose()) {
            return;
        }
        if (event.target === this.dialogRef().nativeElement) {
            this.closeAndCancel();
        }
    }

    protected onCancelEsc(event: Event) {
        if (this.disableBackdropClose()) {
            event.preventDefault();
            return;
        }
        this.cancelled.emit();
        this.closed.emit();
    }

    protected onConfirm() {
        this.confirmed.emit();
        this.closed.emit();
    }

    protected closeAndCancel() {
        this.cancelled.emit();
        this.closed.emit();
    }
}
